import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sentry } from "@/lib/sentry";
import { phCapture } from "@/lib/posthog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { HelpCircle, Info } from "lucide-react";
import OnboardingModal from "@/components/OnboardingModal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useUserPlan } from "@/hooks/useUserPlan";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const isValidLink = (url: string) => url.includes("amazon.co.uk");

const EVIDENCE_SOURCES = [
  { id: "reddit", label: "Reddit", defaultOn: true },
  { id: "youtube", label: "YouTube", defaultOn: true },
  { id: "web", label: "Web (blogs/forums/review sites)", defaultOn: true },
  { id: "competitor_websites", label: "Competitor websites", defaultOn: true },
];

const Generate = () => {
  const navigate = useNavigate();
  const { session, user, loading } = useAuth();

  useEffect(() => { phCapture("generate_page_viewed", { route: "/generate" }); }, []);

  // Clear any protected resume state for signed-out users
  useEffect(() => {
    if (!loading && !user) {
      // Remove any stale auth_return_to that points to protected routes
      try {
        const stale = sessionStorage.getItem("auth_return_to");
        if (stale && (stale.startsWith("/processing") || stale.startsWith("/report") || stale.startsWith("/history"))) {
          sessionStorage.removeItem("auth_return_to");
        }
      } catch {}
    }
  }, [loading, user]);

  // Restore safe draft inputs from sessionStorage
  const draftRestored = useRef(false);
  useEffect(() => {
    if (draftRestored.current) return;
    draftRestored.current = true;
    try {
      const raw = sessionStorage.getItem("scopsift_draft");
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft.topic) setTopic(draft.topic);
      if (draft.industryContext) setIndustryContext(draft.industryContext);
      if (draft.searchKeywords) setSearchKeywords(draft.searchKeywords);
      if (draft.templateType) setTemplateType(draft.templateType);
      if (draft.researchType) setResearchType(draft.researchType);
      sessionStorage.removeItem("scopsift_draft");
    } catch {}
  }, []);

  const [category, setCategory] = useState("");
  const [links, setLinks] = useState(["", "", ""]);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [pastedAmazonReviews, setPastedAmazonReviews] = useState("");
  const [topic, setTopic] = useState("");
  const [researchType, setResearchType] = useState("product");

  const [evidenceSources, setEvidenceSources] = useState<Record<string, boolean>>(
    Object.fromEntries(EVIDENCE_SOURCES.map((s) => [s.id, s.defaultOn]))
  );
  const [searchKeywords, setSearchKeywords] = useState("");
  const [brandName, setBrandName] = useState("");
  const [competitorWebsiteUrls, setCompetitorWebsiteUrls] = useState(["", "", ""]);
  const [industry, setIndustry] = useState("");
  const [templateType, setTemplateType] = useState("Amazon listing copy pack");
  const [industryContext, setIndustryContext] = useState("");
  const [excludeMedical] = useState(true);
  const [marketRadar, setMarketRadar] = useState(true);
  const [priceRadar, setPriceRadar] = useState(false);
  const [priceRadarMarket, setPriceRadarMarket] = useState("UK");

  // Admin debug state (session-only)
  const [debugInfo, setDebugInfo] = useState<{ requestId: string; failStep?: string; failMessage?: string } | null>(null);
  
  const { plan, reportCount, limit, canGenerate, loading: planLoading, isAdmin } = useUserPlan();

  // Onboarding modal (localStorage-only, first-time users)
  const topicRef = useRef<HTMLInputElement>(null);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try {
      const completed = localStorage.getItem("scopsift_onboarding_v2_completed");
      const dismissed = localStorage.getItem("scopsift_onboarding_v2_dismissed");
      return completed !== "1" && dismissed !== "1";
    } catch { return true; }
  });

  // Skip onboarding entirely for signed-in Pro users
  useEffect(() => {
    if (!planLoading && plan === "pro") {
      setShowOnboarding(false);
    }
  }, [planLoading, plan]);
  const closeOnboarding = (product?: string) => {
    setShowOnboarding(false);
    if (product && !topic) {
      setTopic(product);
    }
    setTimeout(() => topicRef.current?.focus(), 100);
  };

  // Admin-only debug logging
  useEffect(() => {
    if (!isAdmin) return;
    let onboardedFlagRead = "unknown";
    try {
      const c = localStorage.getItem("scopsift_onboarding_v2_completed") ?? "null";
      const d = localStorage.getItem("scopsift_onboarding_v2_dismissed") ?? "null";
      onboardedFlagRead = `completed=${c}, dismissed=${d}`;
    } catch { onboardedFlagRead = "storage_blocked"; }
    console.log("[ScopSift Debug]", { authed: !!user, onboardedFlagRead, shouldDisplay: showOnboarding });
  }, [isAdmin, user, showOnboarding]);
  const handleOnboardingAuth = () => {
    sessionStorage.setItem("auth_return_to", "/generate");
    navigate("/signin?returnTo=/generate", { state: { returnTo: "/generate" } });
  };

  const toggleSource = (id: string) => {
    setEvidenceSources((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!topic.trim()) e.topic = "Please tell us what you're selling or researching.";
    if (!industryContext.trim()) e.industryContext = "Industry is required.";

    links.forEach((l, i) => {
      if (l.trim() && !isValidLink(l)) e[`link${i}`] = "Please paste a full Amazon UK product URL (amazon.co.uk).";
    });

    competitorWebsiteUrls.forEach((u, i) => {
      if (u.trim() && !u.trim().startsWith("http")) e[`compUrl${i}`] = "Please paste a full URL starting with http.";
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submittingRef = useRef(false);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!session) {
      // Save safe draft inputs before redirecting
      try {
        sessionStorage.setItem("scopsift_draft", JSON.stringify({
          topic, industryContext, searchKeywords, templateType, researchType,
        }));
      } catch {}
      toast("Please sign in to generate a report.");
      navigate("/signin?returnTo=/generate", { state: { returnTo: "/generate" } });
      return;
    }
    if (!validate()) return;
    if (submittingRef.current) return; // prevent double-submit
    submittingRef.current = true;
    setSubmitting(true);

    const requestId = crypto.randomUUID();
    console.log("[Generate] requestId=" + requestId);
    setDebugInfo({ requestId });
    Sentry.setTag("goal", researchType);

    try {
      const filledLinks = links.filter((l) => l.trim());
      const filledCompUrls = competitorWebsiteUrls.filter((u) => u.trim());

      const trimmedPasted = pastedAmazonReviews.trim();
      const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
      const trackingKey = `${normalize(topic)}||${normalize(industryContext)}||${normalize(templateType)}||${normalize(researchType)}`;

      // Server creates the report row; we await to get the reportId back
      const { data: fnData, error: fnError } = await supabase.functions.invoke("generate-report-real", {
        body: {
          requestId,
          marketplace: "UK",
          product_category: category || "Other",
          competitor_links: filledLinks.length > 0 ? filledLinks : [],
          notes: notes || "",
          search_keywords: searchKeywords.trim(),
          brand_name: brandName.trim() || null,
          evidence_sources: evidenceSources,
          competitor_website_urls: filledCompUrls.length > 0 ? filledCompUrls : [],
          ...(industry.trim() ? { industry: industry.trim() } : {}),
          ...(templateType ? { template_type: templateType } : {}),
          ...(trimmedPasted ? { pasted_amazon_reviews: trimmedPasted } : {}),
          industry_context: industryContext.trim(),
          exclude_medical: excludeMedical,
          topic: topic.trim(),
          research_type: researchType,
          market_radar: marketRadar,
          tracking_key: trackingKey,
          price_radar_enabled: priceRadar,
          price_radar_market: priceRadarMarket,
          plain_english: true,
        },
      });

      if (fnError) {
        console.error(`[Generate] requestId=${requestId} invoke error`, fnError);
        const enriched: any = new Error(`invoke: ${fnError.message}`);
        enriched.step = "invoke";
        throw enriched;
      }

      // The edge function returns { ok: false, step, code, message } on insert failure
      if (fnData && fnData.ok === false) {
        console.error(`[Generate] requestId=${requestId} server error`, fnData);
        const enriched: any = new Error(`${fnData.step}: ${fnData.message} (${fnData.code})`);
        enriched.step = fnData.step || "server";
        throw enriched;
      }

      // Get reportId from server response
      const reportId = fnData?.reportId;
      if (!reportId) {
        const enriched: any = new Error("Server did not return a reportId");
        enriched.step = "missing_report_id";
        throw enriched;
      }

      phCapture("report_generation_started", {
        goal_selected: researchType,
        product_name_present: !!topic.trim(),
        plan,
      });
      navigate(`/processing/${reportId}`);
    } catch (err: any) {
      const step = err?.step || "unknown";
      const msg = err?.message || "Unknown error";
      console.error("[Generate] requestId=" + requestId + " step=" + step, err);
      Sentry.captureException(err, { tags: { requestId, step } });
      setDebugInfo({ requestId, failStep: step, failMessage: msg });
      toast.error(`Report generation failed: ${step} (${msg}). Error ID: ${requestId.slice(0, 8)}`);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const isValid = topic.trim() && industryContext.trim();

  return (
    <Layout>
      <OnboardingModal open={showOnboarding} onClose={closeOnboarding} user={user} onNavigateToAuth={handleOnboardingAuth} isAdmin={isAdmin} />
      <div className="dark-page min-h-[80vh]">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          {/* Page heading */}
          <div className="mx-auto max-w-xl text-center mb-8">
            <h1 className="text-4xl font-extrabold tracking-tight text-white">Generate Copy Pack</h1>
            <p className="mt-2 text-sm leading-7 text-slate-200/90">Configure evidence sources and keywords to analyse.</p>
            <button
              type="button"
              onClick={() => setShowOnboarding(true)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-teal-400/80 hover:text-teal-300 transition-colors"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              What is this?
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem("scopsift_onboarding_v2_completed");
                  localStorage.removeItem("scopsift_onboarding_v2_dismissed");
                  localStorage.removeItem("scopsift_onboarding_v2_goal");
                  localStorage.removeItem("scopsift_onboarding_v2_product");
                  setShowOnboarding(true);
                }}
                className="ml-3 text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                Replay preview
              </button>
            )}
          </div>

          <div className="mx-auto max-w-xl">
            {!canGenerate && !planLoading && (
              <Alert className="mb-6 border-amber-500/30 bg-amber-500/5">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <AlertTitle className="text-amber-300">Report limit reached</AlertTitle>
                <AlertDescription className="text-slate-300">
             You've hit your {plan} plan limit ({limit} reports). <Link to="/pricing" className="text-teal-400 underline hover:text-teal-300" onClick={() => phCapture("upgrade_clicked", { source: "generate" })}>Upgrade to Pro</Link> for 25 reports per month, PDF export, Market Radar, and more.
                </AlertDescription>
              </Alert>
            )}
            <div className="card-surface">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 1. What are you selling or researching? */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-slate-200">What are you selling or researching? *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="inline-flex">
                          <Info className="h-3.5 w-3.5 text-teal-400/70 cursor-help" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="max-w-xs text-xs space-y-1 dark-surface border" side="top">
                        <p className="text-slate-200">This is the thing you want the copy written for — a product you sell now or one you're researching.</p>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Input
                    ref={topicRef}
                    placeholder="e.g., NAD+ supplement, standing desk, resistance bands"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="dark-input border"
                  />
                  <p className="text-xs text-slate-400">Not selling yet? That's fine — use this to research demand and write stronger listing copy later.</p>
                  {errors.topic && <p className="text-sm text-destructive">{errors.topic}</p>}
                </div>

                {/* 2. Industry */}
                <div className="space-y-2">
                  <Label className="text-slate-200">Industry *</Label>
                  <Input
                    placeholder="e.g., fitness accessories, supplements, athleisure"
                    value={industryContext}
                    onChange={(e) => setIndustryContext(e.target.value)}
                    className="dark-input border"
                  />
                  <p className="text-xs text-slate-400">Helps focus the evidence search on your market.</p>
                  {errors.industryContext && <p className="text-sm text-destructive">{errors.industryContext}</p>}
                </div>

                {/* 3. Search keywords */}
                <div className="space-y-2">
                  <TooltipProvider>
                    <div className="flex items-center gap-1">
                      <Label className="text-slate-200">Search keywords *</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Use product nicknames + what it's used for + 2–3 attributes people care about.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                  <Textarea
                    placeholder="e.g., resistance bands snap, roll up, door anchor breaks"
                    value={searchKeywords}
                    onChange={(e) => setSearchKeywords(e.target.value)}
                    className="min-h-[4.5rem] max-h-[12rem] resize-y dark-input border"
                  />
                  <p className="text-xs text-slate-400">
                    Use product nicknames + what it's used for + 2–3 attributes people care about.
                  </p>
                  <div className="text-xs text-slate-400 rounded-xl dark-surface-alt border px-3 py-2 space-y-1">
                    <p className="font-medium">Suggested examples:</p>
                    <p>• pimple patches, hydrocolloid, invisible, under makeup, overnight</p>
                    <p>• yoga leggings, squat proof, high waist</p>
                    <p>• NAD+ supplement, energy support, capsules</p>
                    <p>• resistance bands, home workout, strength training</p>
                  </div>
                  {errors.searchKeywords && <p className="text-sm text-destructive">{errors.searchKeywords}</p>}
                </div>

                {/* 4. Report format */}
                <div className="space-y-2">
                  <TooltipProvider>
                    <div className="flex items-center gap-1">
                      <Label className="text-slate-200">Report format</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Select the style of report you want (e.g., Amazon listing copy vs general marketing).</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                  <Select value={templateType} onValueChange={setTemplateType}>
                    <SelectTrigger className="dark-input border">
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent className="dark-surface border">
                      <SelectItem value="Amazon listing copy pack">Amazon listing copy pack</SelectItem>
                      <SelectItem value="Generic marketing messaging pack">Generic marketing messaging pack</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">Choose what you want ScopSift to generate.</p>
                </div>

                {/* 5. Offer type */}
                <div className="space-y-2">
                  <Label className="text-slate-200">Offer type</Label>
                  <Select value={researchType} onValueChange={setResearchType}>
                    <SelectTrigger className="dark-input border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark-surface border">
                      <SelectItem value="product">Product</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">Choose "Service" for consulting, SaaS, agencies, etc.</p>
                </div>


                {/* Market Radar */}
                <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 px-4 py-3">
                  <div className="grid gap-1 leading-none">
                    <label htmlFor="market-radar" className="text-sm font-medium cursor-pointer text-white">
                      Compare to last run (Market Radar)
                    </label>
                    <p className="text-xs text-slate-400">
                      If ON, we'll compare this report against your most recent run for the same Product/Offer + Industry.
                    </p>
                  </div>
                  <Switch
                    id="market-radar"
                    checked={marketRadar}
                    onCheckedChange={(v) => setMarketRadar(v)}
                  />
                </div>

                {/* Price Radar */}
                <div className="space-y-3 rounded-xl border border-white/10 px-4 py-3">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={priceRadar}
                      onCheckedChange={(v) => setPriceRadar(v === true)}
                      className="mt-0.5"
                    />
                    <div className="grid gap-1 leading-none">
                      <span className="text-sm font-medium text-white">Include Retail Price Radar (web)</span>
                      <p className="text-xs text-slate-400">
                        Finds a snapshot of public retail prices across the web with source links. Not wholesale.
                      </p>
                    </div>
                  </label>
                  {priceRadar && (
                    <div className="space-y-2 pl-6">
                      <Label className="text-slate-200">Market</Label>
                      <Select value={priceRadarMarket} onValueChange={setPriceRadarMarket}>
                        <SelectTrigger className="dark-input border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="dark-surface border">
                          <SelectItem value="UK">UK</SelectItem>
                          <SelectItem value="US">US</SelectItem>
                          <SelectItem value="EU">EU</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* 6. Brand Name */}
                <div className="space-y-2">
                  <Label className="text-slate-200">Brand Name (optional)</Label>
                  <Input
                    placeholder="Your brand or competitor brand name"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    className="dark-input border"
                  />
                </div>

                {/* 7. Evidence Sources */}
                <div className="space-y-3">
                  <Label className="text-slate-200">Evidence Sources</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {EVIDENCE_SOURCES.map((src) => (
                      <label key={src.id} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={evidenceSources[src.id]}
                          onCheckedChange={() => toggleSource(src.id)}
                        />
                        <span className="text-sm text-white">{src.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 8a. Competitor Website URLs (collapsible) */}
                {evidenceSources.competitor_websites && (
                  <Collapsible>
                    <CollapsibleTrigger asChild>
                      <Button type="button" variant="ghost" className="w-full justify-start text-sm text-slate-400 px-0 hover:text-white">
                        ▶ Competitor website URLs (optional)
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-2 pt-2">
                      <p className="text-xs text-slate-400">Paste competitor product pages if they have reviews/FAQs</p>
                      {competitorWebsiteUrls.map((url, i) => (
                        <div key={i}>
                          <Input
                            placeholder="https://competitor.com/product-page"
                            value={url}
                            onChange={(e) => {
                              const next = [...competitorWebsiteUrls];
                              next[i] = e.target.value;
                              setCompetitorWebsiteUrls(next);
                            }}
                            className="dark-input border"
                          />
                          {errors[`compUrl${i}`] && <p className="text-sm text-destructive">{errors[`compUrl${i}`]}</p>}
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* 8b. Amazon Links (collapsible) */}
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button type="button" variant="ghost" className="w-full justify-start text-sm text-slate-400 px-0 hover:text-white">
                      ▶ Amazon competitor links (optional)
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 pt-2">
                    <p className="text-xs text-slate-400">Used for listing-claims analysis. Leave blank to skip.</p>
                    {links.map((link, i) => (
                      <div key={i}>
                        <Input
                          placeholder="https://www.amazon.co.uk/dp/..."
                          value={link}
                          onChange={(e) => {
                            const next = [...links];
                            next[i] = e.target.value;
                            setLinks(next);
                          }}
                          className="dark-input border"
                        />
                        {errors[`link${i}`] && <p className="text-sm text-destructive">{errors[`link${i}`]}</p>}
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>

                {/* 8c. Pasted Reviews / Complaints (collapsible) */}
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button type="button" variant="ghost" className="w-full justify-start text-sm text-slate-400 px-0 hover:text-white">
                      ▶ Paste negative reviews / complaints (optional — best results)
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 pt-2">
                    <p className="text-xs text-slate-400">
                      Paste low-star reviews, complaints, refund reasons, support messages, or Reddit comments. We'll cluster themes and generate copy.
                    </p>
                    <Textarea
                      placeholder="Copy a block of low-star reviews from Amazon and paste here…"
                      value={pastedAmazonReviews}
                      onChange={(e) => setPastedAmazonReviews(e.target.value.slice(0, 20000))}
                      maxLength={20000}
                      className="min-h-[120px] dark-input border"
                    />
                    <div className="flex justify-between">
                      <p className="text-xs text-slate-400">{pastedAmazonReviews.length.toLocaleString()}/20,000</p>
                    </div>
                    <p className="text-xs text-destructive/80">Do not paste personal information (names, emails, addresses, phone numbers).</p>
                    <div className="rounded-xl dark-surface-alt border px-3 py-2 text-xs text-slate-400 space-y-1 mt-2">
                      <p className="font-medium">How to paste low-star reviews fast (30 seconds):</p>
                      <p>1) Open a competitor product on Amazon → click the star rating to open the reviews page.</p>
                      <p>2) Filter to 1–3★ (or Critical) and Most recent if available.</p>
                      <p>3) Highlight a block of review text (10–30 reviews is plenty) → Copy → Paste here.</p>
                      <p className="italic">Tip: Don't worry about formatting — one big paste is fine.</p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* 9. Product notes */}
                <div className="space-y-2">
                  <Label className="text-slate-200">Product notes (optional)</Label>
                  <Textarea
                    placeholder="Any details about your product…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                    maxLength={500}
                    className="dark-input border"
                  />
                  <p className="text-xs text-slate-400">
                    Add facts we should respect (materials, guarantees, price point, who it's for). Helps avoid generic or incorrect copy.
                  </p>
                  <p className="text-xs text-slate-400">{notes.length}/500</p>
                </div>

                {!user && (
                  <Alert className="border-teal-400/30 bg-teal-400/5">
                    <Info className="h-4 w-4 text-teal-400" />
                    <AlertDescription className="text-sm text-slate-300">
                      Free account required to generate reports.{" "}
                      <Link to="/auth?returnTo=/generate" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                        Sign up free →
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full btn-gradient" disabled={!isValid || submitting || !canGenerate}>
                  {submitting ? "Generating…" : !canGenerate ? "Report limit reached" : "Generate"}
                </Button>
              </form>

              {/* Admin Debug Panel — session-only, no DB storage */}
              {isAdmin && debugInfo && (
                <details className="mt-4 rounded-lg border border-white/10 p-3 text-xs">
                  <summary className="cursor-pointer text-slate-400 font-medium">Debug info (admin only)</summary>
                  <div className="mt-2 space-y-1 text-slate-300 font-mono">
                    <p>Request ID: {debugInfo.requestId}</p>
                    {debugInfo.failStep && <p>Failed step: {debugInfo.failStep}</p>}
                    {debugInfo.failMessage && <p>Error: {debugInfo.failMessage}</p>}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Generate;
